import Tournament from '@/models/Tournament';
import { TournamentSystem, ParticipantsMode, WinCondition, TournamentStatus, GameStage, GameStageLabel, GameResult, SeasonTiebreak, TerminationWithdrawal, TerminationWin, TieBreakCoefficientLabel, TournamentCalculateType, TeamMode } from '@/services/utils/enums';
import { makeLogger } from '@/services/utils/logger';
import { toRaw } from 'vue';
import { translate } from '@/i18n';
// ─── Logging ───────────────────────────────────────────────────────────────
const DEBUG_LOG = false;
const log = makeLogger('GlobalTools', DEBUG_LOG);
// ───────────────────────────────────────────────────────────────────────────
import { Glicko2 } from 'glicko2';

export const normalizePlayerName = value => (value || '').trim().replace(/\s+/g, ' ');

export { resolveMediaUrl } from './imageVariants';

export function validatePlayerName(value, required = false) {
  const name = normalizePlayerName(value);
  if (!name && !required) return '';
  if (name.length < 2) return translate('validation.minimumTwoCharacters');
  if (!/^\p{L}[\p{L}\p{M}\p{Nd} '\-.]*$/u.test(name)) {
    return translate('validation.playerNameCharacters');
  }
  return '';
}

/**
 * Выполняет функцию с локальным лоадером и параллельной задержкой.
 * @param {string} key - Ключ для отображения лоадера.
 * @param {Promise} dataPromise - Параллельная загрузка данных.
 * @param {Function} func - Основная функция, возвращающая Promise (внутри withLocalLoader).
 * @param {number} delayMs - Задержка в мс (по умолчанию 0).
 * @returns {Promise<any>} - Результат dataPromise.
 */
export async function withParallelLoader(key, dataPromise, func = async () => {}, delayMs = 0) {
  const delay = delayMs > 0 ? new Promise(resolve => setTimeout(resolve, delayMs)) : Promise.resolve();
  const result = await withLocalLoader(key, async () => {
    const resultPromise = await dataPromise;
    await func(resultPromise);
    await delay;
    return resultPromise;
  });
  return result;
}

/**
 * Показывает локальный лоадер по ключу (например, "table") на время async-функции
 * @param {string} key - ключ (например, 'table', 'button1', 'block-X')
 * @param {Function} promiseFn - асинхронная функция
 * @returns {Promise<any>} Результат выполнения promiseFn
 */
export async function withLocalLoader(key, promiseFn) {
  const loader = window.app?.config?.globalProperties?.$loader;
  loader.setButtonLoading(key, true);
  try {
    return await promiseFn();
  } finally {
    loader.setButtonLoading(key, false);
  }
}

/**
 * Вкладывает загруженные текущие рейтинги в players[*].ratingsMap турнира.
 * Для frozen приоритет остаётся у snapshot, если он уже есть в ratingsMap.
 * Для rolling записываются только переданные текущие рейтинги.
 * @param {Object} tournament
 * @param {Array} ratingRows
 * @param {string|string[]|null} [controlTypes=null]
 * @returns {Object|null}
 */
export function applyPlayerRatings(tournament, ratingRows = [], controlTypes = null) {
  if (!tournament || !Array.isArray(tournament.players) || tournament.players.length === 0) {
    return tournament || null;
  }

  const controls = Array.isArray(controlTypes)
    ? controlTypes.filter(Boolean)
    : controlTypes
      ? [controlTypes]
      : [];

  const currentByKey = new Map();
  const rows = Array.isArray(ratingRows) ? ratingRows : [];

  for (const row of rows) {
    const data = row?.attributes || row;
    const playerNode = data?.player?.data || data?.player;
    const playerDocId = playerNode?.documentId || playerNode?.attributes?.documentId;
    const controlType = data?.controlType;
    if (!playerDocId || !controlType) continue;

    currentByKey.set(`${playerDocId}|${controlType}`, {
      rating: Number(data.rating),
      games: Number(data.games),
      peak: Number(data.peak),
      provisional: Boolean(data.provisional),
    });
  }

  const preferSnapshot = tournament.calculateType === TournamentCalculateType.FROZEN;
  for (const player of tournament.players) {
    if (!player?.id) continue;

    const snapshotMap = (player.ratingsMap && typeof player.ratingsMap === 'object')
      ? { ...player.ratingsMap }
      : {};
    const targetControls = controls.length > 0
      ? controls
      : Array.from(new Set([
          ...Object.keys(snapshotMap),
          ...Array.from(currentByKey.keys())
            .filter((key) => key.startsWith(`${player.id}|`))
            .map((key) => key.split('|')[1])
        ]));

    player.ratingsMap = player.ratingsMap && typeof player.ratingsMap === 'object'
      ? player.ratingsMap
      : {};

    for (const controlType of targetControls) {
      if (!controlType) continue;

      const current = currentByKey.get(`${player.id}|${controlType}`) || null;
      const snapshot = snapshotMap[controlType] || null;

      if (preferSnapshot) {
        if (snapshot) {
          player.ratingsMap[controlType] = snapshot;
        } else if (current) {
          player.ratingsMap[controlType] = current;
        } else {
          delete player.ratingsMap[controlType];
        }
        continue;
      }

      if (current) {
        player.ratingsMap[controlType] = current;
      } else {
        delete player.ratingsMap[controlType];
      }
    }
  }

  return tournament;
}

/**
 * Показывает глобальный лоадер на время выполнения async-функции
 * @param {Function} promiseFn - асинхронная функция
 * @returns {Promise<any>} Результат выполнения promiseFn
 */
export async function withGlobalLoader(promiseFn) {
  const loader = window.app?.config?.globalProperties?.$loader;
  loader.showGlobal();
  try {
    return await promiseFn();
  } finally {
    loader.hideGlobal();
  }
}

/**
 * Функция перемешивания массива по алгоритму Фишера-Йейтса
 * @param {Array} array - Массив для перемешивания
 */
export function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

/**
 * Округляет число до заданного количества знаков после запятой.
 *
 * @param {number} value - Число, которое нужно округлить.
 * @param {number} [digits=2] - Количество знаков после запятой (по умолчанию 2).
 * @returns {number} Округлённое число.
 */
export function roundTo(value, digits = 2) {
  return Number(value.toFixed(digits));
}

/**
 * Заменяет uuid в заданных полях модели на serverId по справочному списку.
 * @param {Object} model - Объект с полями-связями (с uuid).
 * @param {Object} relationMap - Объект формата { fieldName: relatedItems[] }
 * Пример:
 *   resolveRelations(game, {
 *     whitePlayerIds: tournament.players,
 *     blackPlayerIds: tournament.players,
 *     tournamentId: [tournament] // одиночная связь
 *   });
 *
 * @param {boolean} strict - Если true — выбрасывает ошибку, если не найдёт serverId.
 * @returns {Object} Новый объект с подменёнными serverId.
 */
export function resolveRelations(model, relationMap, strict = false) {
  const result = { ...model };

  for (const [fieldName, relatedList] of Object.entries(relationMap)) {
    const uuid = model[fieldName];
    if (!uuid) continue;

    const items = Array.isArray(relatedList) ? relatedList : [relatedList];
    const game = items.find(item => item.id === uuid);

    if (game && game.serverId) {
      result[fieldName] = game.serverId;
    } else {
      const message = `⚠️ Не удалось найти serverId для связи "${fieldName}" по uuid: ${uuid}`;
      if (strict) throw new Error(message);
      log.warn('resolveRelations', message);
      // оставляем как есть (uuid)
    }
  }

  return result;
}

/**
 * Формирует объект с отформатированными настройками турнира для отображения
 * @param {Object} tournament - Объект турнира
 * @returns {Object} Отформатированный объект настроек турнира
 */
export function getFormattedSettings(tournament) {
  const t = tournament;
  const players = t.players ?? [];

  const tModel = (t instanceof Tournament) ? t : Tournament.fromObject(t);
  const swiss = tModel.getSwissSettings ? tModel.getSwissSettings() : null;
  const elimination = tModel.getEliminationSettings ? tModel.getEliminationSettings() : null;

  const participantsCount =
    t.playersCount != null
      ? t.playersCount
      : Array.isArray(t.players) && t.players.length > 0
        ? t.players.length
        : '—';
  const totalGamesCount = 
    t.gamesCount ? t.gamesCount : 
    tModel.games?.swiss?.length > 0 ? tModel.getRegularGames().length + tModel.getEliminationGames().length 
    : 0;
  const systemKeys = {
    [TournamentSystem.SWISS]: 'swiss',
    [TournamentSystem.ELIMINATION]: 'elimination',
    [TournamentSystem.ROUND_ROBIN]: 'roundRobin',
    [TournamentSystem.MIXED]: 'mixed',
  };
  const stageLabel = (stage) => stage
    ? translate(`playoffStages.${stage}`)
    : stage;

  const result = {
    system: translate(`formattedSettings.systems.${systemKeys[t.system] || 'mixed'}`),
    extraSystemNote: t.system === TournamentSystem.MIXED ? translate('formattedSettings.mixedSwiss') : null,
    swiss: null,
    elimination: null,
    bracketsInfo: null,
    status: {
      code: t.status,
      label: translate(`tournamentStatuses.${t.status}`) || TournamentStatus[t.status] || t.status
    },
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
    date: t.date,
    finishedAt: t.finishedAt,
    participants: players.map(p => p.name),
    participantsCount: participantsCount,
    roundsPlayed: tModel.getMaxRound(),
    totalGames: totalGamesCount,
    regularGames: tModel.getRegularGames().length,
    eliminationGames: tModel.getEliminationGames().length,
    lastEliminationStage: (() => {
      const games = tModel.getEliminationGames();
      const playedGames = games.filter(m => m.result);
      const stageCode = playedGames.length > 0
        ? playedGames.reduce((prev, curr) => (curr.level ?? 0) > (prev.level ?? 0) ? curr : prev, playedGames[0]).stage
        : null;
      return {
        code: stageCode,
        label: stageLabel(stageCode) || GameStageLabel[stageCode] || stageCode
      };
    })(),
    lastStageLabel: (() => {
      const eliminationGames = tModel.getEliminationGames().filter(m => m.result);
      if (eliminationGames.length > 0) {
        const maxLevel = Math.max(...eliminationGames.map(m => m.level ?? 0), 0);
        const game = eliminationGames.find(m => m.level === maxLevel);
        return stageLabel(game?.stage) || GameStageLabel[game?.stage] || game?.stage;
      }

      const maxRound = tModel.getMaxRound();
      return translate('formattedSettings.swissRound', { round: maxRound });
    })()
  };

  if ((t.system === TournamentSystem.SWISS || t.system === TournamentSystem.MIXED || t.system === TournamentSystem.ROUND_ROBIN) && swiss) {
    result.swiss = {
      rounds: swiss.rounds,
      time: translate('formattedSettings.timeSeconds', { minutes: swiss.timeMinutes, increment: swiss.increment }),
      coefficient: TieBreakCoefficientLabel[swiss.tieBreakCoefficient] || swiss.tieBreakCoefficient,
    };
  }

  if ((t.system === TournamentSystem.ELIMINATION || t.system === TournamentSystem.MIXED) && elimination) {
    const timeSwiss = swiss ? (swiss.timeMinutes + '+' + swiss.increment) : null;
    const timeElim = elimination.timeMinutes + '+' + elimination.increment;
    const sameTime = timeSwiss != null && timeSwiss === timeElim;

    const bracketsType = elimination.bracketsType;
    const top = elimination.participantsTop;
    const bottom = elimination.participantsBottom;
    const mode = elimination.participantsMode;

    result.elimination = {
      timeNote: sameTime ? translate('formattedSettings.sameAsSwiss') : null,
      timeValue: timeElim,
      countToWin: elimination.winCondition === WinCondition.GAMES
        ? translate('formattedSettings.gamesToWin', elimination.countToWin, { count: elimination.countToWin })
        : translate('formattedSettings.pointsToWin', elimination.countToWin, { count: elimination.countToWin }),
      colorRule: {
        code: elimination.colorRule,
        label: (() => {
          switch (elimination.colorRule) {
            case 'leaders_white': return translate('newTournament.settings.leadersWhite');
            case 'leaders_black': return translate('newTournament.settings.leadersBlack');
            case 'random': return translate('newTournament.settings.colorsRandom');
            default: return elimination.colorRule;
          }
        })()
      },
      tieBreaker: {
        code: elimination.tieBreakerRule
      },
    };

    result.bracketsInfo = {
      type: bracketsType === 'one'
        ? translate('formattedSettings.oneBracket')
        : translate('formattedSettings.twoBrackets'),
      splitMode: (() => {
        if (bracketsType === 'top-bottom') {
          if (mode === ParticipantsMode.SPLIT) return translate('newTournament.settings.splitEvenly');
          if (mode === ParticipantsMode.MANUAL_MANUAL) return translate('formattedSettings.manualSelection');
          if (mode === ParticipantsMode.MANUAL_REMAINING) return translate('formattedSettings.remainingParticipants');
        }
        return null;
      })(),
      topCount: top,
      bottomCount: bottom
    };
  }

  log('getFormattedSettings', '📊 Итоговый объект настроек турнира:', result);
  return result;
}

export { formatDate, formatDateTime, formatTournamentDate } from './dateFormat';
function _trimNbspRight(s) {
  return typeof s === 'string' ? s.replace(/[\s\u00A0]+$/u, '') : '';
}

// ===== Formatting for list (human-readable) =====
function translatedRuleLabel(translate, key, fallback, params) {
  return typeof translate === 'function' ? translate(`seasonFormula.${key}`, params) : fallback;
}

function fieldLabel(field, translate) {
  const map = { place: 'Место', points: 'Очки', groupPlace: 'Место в группе' };
  return map[field]
    ? translatedRuleLabel(translate, `fields.${field}`, map[field])
    : String(field || '');
}

function opSymbol(op) {
  const map = { eq: '=', ne: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤' };
  return map[op] || op;
}

function formatSingleFilter(flt, translate) {
  if (!flt) return '';
  if (flt.op === 'eqEachOther') {
    return translatedRuleLabel(translate, 'pointsEqual', 'Очки равны');
  }

  const left = fieldLabel(flt.field, translate);
  const op = opSymbol(flt.op);

  if (flt.valueMode === 'placeRef') {
    const ref = (flt.placeRef || '').toString().trim().toUpperCase();
    const placeReference = translatedRuleLabel(
      translate,
      'placeReference',
      `${left} ${op} как у места ${ref}`,
      { field: left, operator: op, place: ref }
    );
    return placeReference.replace(/[\s\u00A0]+$/u, '');
  }
  const val = (flt.value || '').toString().trim().toUpperCase();
  return (`${left} ${op} ${val}`).replace(/[\s\u00A0]+$/u, '');
}

export function formatRuleFormula(rule, translate) {
  if (!rule) return '';
  const t = rule.target || {};

  if (t.type === 'filter') {
    const logic = (t.filtersLogic || 'AND').trim().toUpperCase();
    const items = (t.filters || []).map(f => formatSingleFilter(f, translate)).filter(Boolean);
    const s = items.join(` ${logic} `);
    return (s || translatedRuleLabel(translate, 'filterMissing', 'Фильтр не задан')).replace(/[\s\u00A0]+$/u, '');
  }

  if (t.type === 'allPlayers') return translatedRuleLabel(translate, 'allPlayers', 'Все игроки');
  if (t.type === 'topN') {
    return translatedRuleLabel(translate, 'topN', `Топ-${t.n ?? ''}`.trim(), { count: t.n ?? '' });
  }
  if (t.type === 'placeRange') {
    return translatedRuleLabel(
      translate,
      'placeRange',
      `Место ${t.from ?? ''}–${t.to ?? ''}`,
      { from: t.from ?? '', to: t.to ?? '' }
    );
  }

  if (t.field && t.op) return formatSingleFilter(t, translate);
  return '';
}

/**
 * Преобразует идентификатор правила в строку для note вида "rule: <id>".
 * @param {string|number} ruleId
 * @returns {string}
 */
/**
 * Универсальный помощник для снятия реактивной оболочки Vue (toRaw) с любой сущности.
 *
 * Пример:
 *   const season = toRawEntity(this.tournament?.season);
 *
 * Если значение не является объектом/прокси, возвращает его как есть.
 *
 * @template T
 * @param {T} entity
 * @returns {T}
 */
export function toRawEntity(entity) {
  if (!entity || typeof entity !== 'object') return entity;
  return toRaw(entity);
}

/**
 * Расчёт нового рейтинга игрока по формуле Эло
 * @param {number} currentRating - текущий рейтинг игрока
 * @param {number} opponentRating - рейтинг соперника
 * @param {number} result - результат партии (1 - победа, 0.5 - ничья, 0 - поражение)
 * @param {number} kFactor - коэффициент K
 * @returns {{ newRating: number, delta: number }} - новый рейтинг и изменение
 */
export function calculateElo(currentRating, opponentRating, result, kFactor) {
  // Ограничиваем разницу рейтингов до 400 пунктов в обе стороны
  
  const ratingDiff = opponentRating - currentRating;
  const expectedScore = getExpectedScoreFromTable(ratingDiff);
  if (!expectedScore) log.error('calculateElo', 'expectedScore is empty', { expectedScore, diff: ratingDiff });
  const delta = roundTo(kFactor * (result - expectedScore));
  const newRating = Math.max(800, roundTo(currentRating + delta));
  return { newRating, delta };
}

function getExpectedScore(diff) {
  const cappedDiff = Math.max(-400, Math.min(400, diff));
  return 1 / (1 + Math.pow(10, cappedDiff / 400));
}

export function getExpectedScoreFromTable(diff) {
  // Ограничиваем разницу до диапазона [-400, 400]
  const cappedDiff = Math.max(-400, Math.min(400, diff));
  const absDiff = Math.abs(cappedDiff);

  const table = [
    [3, 0.50], [10, 0.51], [17, 0.52], [25, 0.53], [32, 0.54], [39, 0.55],
    [46, 0.56], [53, 0.57], [61, 0.58], [68, 0.59], [76, 0.60], [83, 0.61],
    [91, 0.62], [98, 0.63], [106, 0.64], [113, 0.65], [121, 0.66], [129, 0.67],
    [137, 0.68], [145, 0.69], [153, 0.70], [162, 0.71], [170, 0.72], [179, 0.73],
    [188, 0.74], [197, 0.75], [206, 0.76], [215, 0.77], [225, 0.78], [235, 0.79],
    [245, 0.80], [256, 0.81], [267, 0.82], [278, 0.83], [290, 0.84], [302, 0.85],
    [315, 0.86], [328, 0.87], [344, 0.88], [357, 0.89], [374, 0.90], [391, 0.91],
    [411, 0.92]
  ];

  for (const [maxDiff, expected] of table) {
    if (absDiff <= maxDiff) return cappedDiff > 0 ? 1 - expected : expected;
  }

  // return cappedDiff > 0 ? 0.01 : 0.99;
}

/**
 * @param {Object} playerData - Игрок
 * @param {number} playerData.rating
 * @param {number} playerData.rd
 * @param {number} playerData.vol
 * @param {Array} games - Матчи: [{ opponent: { rating, rd, vol }, result }]
 * @param {number} [tau=0.5]
 */
// export function calculateGlicko2(playerData, games, tau = 0.5) {
//   const glicko = new Glicko2({
//     tau,
//     rating: 1500,
//   });

//   const player = glicko.makePlayer(playerData.rating, playerData.rd, playerData.vol);

//   const formattedGames = games.map(({ opponent, result }) => {
//     const opp = glicko.makePlayer(opponent.rating, opponent.rd, opponent.vol);
//     return [player, opp, result];
//   });

//   glicko.updateRatings(formattedGames);

//   const newRating = player.getRating();
//   const newRd = player.getRd();
//   const newVol = player.getVol();

//   return {
//     newRating,
//     newRd,
//     newVol,
//     delta: parseFloat((newRating - playerData.rating).toFixed(2))
//   };
// }



/**
 * Вычисляет новые значения рейтинга Glicko-2 для всех игроков по результатам турнира.
 *
 * @param {Object<string, { rating: number, rd: number, vol: number }>} playersData
 * Объект с данными игроков. Ключ — id игрока, значение — объект с полями `rating`, `rd` (rating deviation), `vol` (volatility).
 *
 * @param {Array<{ playerId: string, opponentId: string, result: number }>} gameResults
 * Список результатов матчей. Каждый объект содержит id игрока, id соперника и результат:
 * - 1 — победа
 * - 0.5 — ничья
 * - 0 — поражение
 *
 * @param {number} [tau=0.5] — Коэффициент стабильности рейтинга (чем выше, тем быстрее изменяется рейтинг).
 *
 * @returns {Array<{
 *   id: string,
 *   newRating: number,
 *   newRd: number,
 *   newVol: number,
 *   delta: number
 * }>} — Массив обновлённых данных для каждого игрока, включая изменение рейтинга `delta`.
 */
export function calculateGlicko2(playersData, gameResults, tau = 0.5) {
  const glicko = new Glicko2({ tau, rating: 1500 });
  const playerMap = {};
  for (const [id, p] of Object.entries(playersData)) {
    playerMap[id] = glicko.makePlayer(p.rating, p.rd, p.vol);
  }

  const games = [];
  for (const { playerId, opponentId, result } of gameResults) {
    const player = playerMap[playerId];
    const opponent = playerMap[opponentId];
    if (player && opponent) {
      games.push([player, opponent, result]);
    }
  }

  glicko.updateRatings(games);

  const res = Object.entries(playerMap).map(([id, p]) => {
    const newRating = p.getRating();
    const newRd = p.getRd();
    const newVol = p.getVol();

    const result = {
      id,
      newRating,
      newRd,
      newVol,
      delta: parseFloat((newRating - playersData[id].rating).toFixed(2)) };
    return result;
  });

  return res;
}

/**
 * Пропорционально распределяет итоговую delta по матчам игрока с учетом результата
 * @param {number} totalDelta - Общая дельта рейтинга игрока за турнир
 * @param {Object} player - Игрок: { rating, rd, vol }
 * @param {Array<Object>} games - Массив матчей: [{ opponent: { rating, rd, vol }, result, gameId }]
 * @returns {Array<{gameId: string, delta: number}>}
 */
export function distributeGlickoDeltaByGames(totalDelta, player, games) {
  if (!games.length) return [];

  // Базовый вес с учетом силы соперника и результата матча
  const weights = games.map(m => {
    const strength = Math.abs(m.opponent.rating - player.rating) || 1;
    const outcomeFactor = m.result === 1 ? 1 : m.result === 0 ? -1 : 0;
    return strength * outcomeFactor;
  });

  const absWeights = weights.map(w => Math.abs(w));
  const weightSum = absWeights.reduce((a, b) => a + b, 0) || 1;

  const rawDeltas = weights.map(w => (Math.abs(w) / weightSum) * totalDelta * Math.sign(w));

  const rounded = rawDeltas.map(d => parseFloat(d.toFixed(4)));
  const correction = parseFloat((totalDelta - rounded.reduce((a, b) => a + b, 0)).toFixed(4));

  if (Math.abs(correction) > 0.0001) {
    // Найдем индекс наибольшей дельты по модулю
    const idx = rounded.findIndex(d => Math.abs(d) === Math.max(...rounded.map(Math.abs)));
    rounded[idx] += correction;
  }

  return games.map((m, i) => ({
    gameId: m.gameId,
    delta: rounded[i],
  }));
}


/**
 * Обновляет RD (rating deviation) игрока при пропуске рейтингового периода.
 *
 * @param {number} rd - Текущее значение RD игрока.
 * @param {number} volatility - Текущее значение волатильности (σ) игрока.
 * @param {number} t - Количество пропущенных рейтинговых периодов.
 * @param {number} [maxRd=350] - Максимально допустимое значение RD.
 * @returns {number} Обновлённое значение RD.
 */
export function getUpdatedRdAfterInactivity(rd, volatility, t, maxRd = 350) {
  const newRd = Math.sqrt(rd * rd + volatility * volatility * t);
  return Math.min(newRd, maxRd);
}

// Константы Glicko-2
const Q = Math.log(10) / 400;
const SCALE = 173.7178;

// g(phi)
function g(phi) {
  return 1 / Math.sqrt(1 + (3 * Q * Q * phi * phi) / (Math.PI * Math.PI));
}

// Ожидание результата
function expected(mu, muOpp, phiOpp) {
  const gj = g(phiOpp);
  return 1 / (1 + Math.exp(-gj * (mu - muOpp)));
}

/**
 * Разложение турнирной дельты игрока по матчам (Glicko-2, один период)
 * @param {Object} playerPre  { rating, rd, vol } до турнира (из playersData)
 * @param {number} playerNewRd RD после турнира (из newRd из вашего результата)
 * @param {Array<{ opp: { rating:number, rd:number }, s:number }>} games
 * @returns {{ perGame: number[], total:number }} — дельты в очках рейтинга
 */
export function glicko2DecomposeTournamentDelta(playerPre, playerNewRd, games) {
  if (!games || games.length === 0) return { perGame: [], total: 0 };

  const mu = (playerPre.rating - 1500) / SCALE;
  const phiStar = playerPre.rd / SCALE;  // предтурнирный RD (уже размытый при входе в период)
  const K = (phiStar * phiStar) * Q;

  const perGame = games.map(gm => {
    const muOpp  = (gm.opp.rating - 1500) / SCALE;
    const phiOpp = gm.opp.rd / SCALE;
    const Ej = expected(mu, muOpp, phiOpp);
    const tj = g(phiOpp) * (gm.s - Ej);
    return {
      gameId: gm.gameId,
      opp: gm.oppId,        // ← id оппонента
      oppRat: gm.oppRat,    // ← его рейтинг ДО турнира
      delta: SCALE * (K * tj)
    };
  });

  const total = perGame.reduce((a, b) => a + b.delta, 0);
  return { perGame, total };
}

/**
 * Универсальный метод закрытия Bootstrap-модалки по id.
 * @param {string} modalId - id DOM-элемента модалки.
 */
export function closeModalById(modalId) {
  try {
    const Modal = window.bootstrap && window.bootstrap.Modal;
    if (!Modal) {
      log.warn('closeModalById', 'Bootstrap Modal API недоступен');
      return;
    }
    if (!modalId) {
      log.warn('closeModalById', 'Не передан id модалки');
      return;
    }
    const el = document.getElementById(modalId);
    if (!el) {
      log.warn('closeModalById', `Элемент с id="${modalId}" не найден`);
      return;
    }
    const instance = typeof Modal.getOrCreateInstance === 'function'
      ? Modal.getOrCreateInstance(el)
      : (Modal.getInstance(el) || new Modal(el));
    instance.hide();
  } catch (e) {
    log.warn('closeModalById', `Не удалось закрыть модалку "${modalId}" программно:`, e);
  }
}

/**
 * Определяет техническое окончание партии по активности белой и чёрной стороны.
 * @param {Object} params
 * @param {string|null} params.whiteSideParticipantId
 * @param {string|null} params.blackSideParticipantId
 * @param {Function} params.isParticipantActive
 * @returns {string|null}
 */
export function getWithdrawalTermination({
  whiteSideParticipantId,
  blackSideParticipantId,
  isParticipantActive,
} = {}) {
  if (!whiteSideParticipantId || !blackSideParticipantId || typeof isParticipantActive !== 'function') {
    return null;
  }

  const whiteActive = isParticipantActive(whiteSideParticipantId);
  const blackActive = isParticipantActive(blackSideParticipantId);

  if (whiteActive && blackActive) {
    return null;
  }

  if (!whiteActive && !blackActive) {
    return TerminationWin.WITHDRAWAL_BOTH;
  }

  return whiteActive
    ? TerminationWin.WITHDRAWAL_BLACK
    : TerminationWin.WITHDRAWAL_WHITE;
}
